import React from 'react';
import './PrivacyPolicy.scss';
import ColoredHeaderLogo from 'assets/headerAssets/svg/ColoredHeaderLogo';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <div className='flex gap-2 justify-center items-center'>
          <div
            className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
          >
            <ColoredHeaderLogo />
          </div>
        </div>
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: February 12, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to InspectXpert ("we," "our," or "us"). We are committed to protecting
            your personal information and your right to privacy. This Privacy Policy explains
            how we collect, use, and share information when you use our application.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li>Account information (name, email, password)</li>
            <li>Profile information (date of birth, phone number, avatar)</li>
            <li>Tickets, meetings, and reminders you create</li>
            <li>Organization and branch details</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li>Log data (IP address, browser type, access times)</li>
            <li>Device information</li>
            <li>Usage data and analytics</li>
          </ul>
        </section>

        <section>
          <h2>3. Google Calendar Integration</h2>
          <p>
            Our application integrates with Google Calendar to help you manage your schedules
            more efficiently. Here's how we use your calendar data:
          </p>

          <h3>3.1 What We Access</h3>
          <ul>
            <li>
              Permission to create, read, update, and delete Google Calendar events that are created through our application using the Google Calendar API
            </li>
            <li>
              Although the granted scope allows access to calendar event data, we only use this access to manage events created through our application
            </li>
            <li>
              We do not use or analyze calendar events that are not created through our application for any purpose
            </li>
          </ul>

          <h3>3.2 How We Use It</h3>
          <ul>
            <li>Automatically sync tickets, meetings, and reminders created in our platform to your Google Calendar</li>
            <li>Update or remove those synced events when changes are made within our application</li>
            <li>Send calendar invitations to other users for shared events created in our platform</li>
          </ul>

          <h3>3.3 Data Storage</h3>
          <ul>
            <li>We store Google Calendar access tokens securely in our encrypted database</li>
            <li>We do not store your actual calendar events on our servers</li>
            <li>We only use tokens to sync events you create in our application</li>
          </ul>

          <h3>3.4 Your Control</h3>
          <ul>
            <li>You can disconnect Google Calendar anytime from your profile settings</li>
            <li>Disconnecting removes all access tokens from our system immediately</li>
            <li>You can control which event types sync (tickets/meetings/reminders) through preferences</li>
            <li>You retain full control of your Google Calendar data</li>
          </ul>

          <h3>3.5 Third-Party Access</h3>
          <ul>
            <li>We do not share your Google Calendar data with third parties</li>
            <li>We do not sell your calendar data</li>
            <li>We only access your calendar to provide the synchronization service you've authorized</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Manage your account and provide customer support</li>
            <li>Send you updates, notifications, and administrative messages</li>
            <li>Synchronize your tasks with Google Calendar</li>
            <li>Analyze usage patterns and optimize our application</li>
            <li>Protect against fraud and abuse</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your
            personal information, including:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure token storage for Google Calendar access</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Monitoring and logging of system access</li>
          </ul>
          <p>
            While we implement strong security measures to protect your data, no system is 100% secure. </p>
        </section>

        <section>
          <h2>6. Data Sharing</h2>
          <p>We do not sell your personal information. We may share your information with:</p>
          <ul>
            <li>
              <strong>Service Providers:</strong> Third-party vendors who help us operate our
              application (e.g., cloud hosting, email services)
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to protect our rights
            </li>
            <li>
              <strong>Organization Members:</strong> Other users within your organization for
              collaboration purposes (tickets, meetings)
            </li>
          </ul>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as
            needed to provide services. You may request deletion of your data at any time by
            contacting us.
          </p>
          <h3>7.1 International Data Transfers</h3>
          <p>
            Your information may be processed and stored in countries other than your own,
            including countries that may have different data protection laws than your jurisdiction.
            By using our services, you consent to such transfers where permitted by law.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Export your data</li>
            <li>Opt-out of certain data collection</li>
            <li>Disconnect Google Calendar integration</li>
          </ul>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our service is not intended for users under the age of 13. We do not knowingly
            collect information from children under 13.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any
            changes by posting the new policy on this page and updating the "Last Updated" date.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <ul>
            <li>Email: sales@synctel.in</li>
            <li>Address: 314, 3rd Floor, 99 Shopping Center, Sayan Rd, opp. Bhagunagar, Brahmani Nagar Society, Amroli</li>
            <li>Phone: +91 6358280506</li>
          </ul>
        </section>

        <section>
          <h2>12. Google API Services User Data Policy</h2>
          <p>
            We only use Google user data to provide and improve features that are visible and directly related to the functionality of our application.
            We do not use Google user data for advertising purposes, user profiling, or any unrelated marketing activities.
            We do not transfer Google user data to third parties except as necessary to provide our services or comply with legal obligations.
            We do not use Google Workspace APIs or Google user data obtained through these APIs
            to develop, improve, or train generalized artificial intelligence (AI) or machine learning (ML) models.
          </p>

          <p>
            Our use of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;